import { request } from '@/utils/ajax';
import { SentryIssue } from '@/components/SystemHealth/dummyData';

// ---- AWS (Elastic Beanstalk + CloudWatch) ----

/** Tab order on the page. */
export const stages = ['test', 'uat', 'prod'] as const;
export type Stage = (typeof stages)[number];

export type Health = 'ok' | 'degraded' | 'down' | 'unknown';

export interface AwsServiceHealth {
    /** EB application name, e.g. bygr-api-core. */
    name: string;
    environmentName: string;
    health: Health;
    /** EB enhanced health status: Ok, Warning, Degraded, Severe, … */
    healthStatus: string | null;
    /** EB environment status: Ready, Updating, Launching, … */
    status: string | null;
    causes: string[];
    instancesOk: number | null;
    instancesTotal: number | null;
    /** Last 15 minutes; null when the environment has no load balancer of its own (or we may not read its metrics). */
    requestsPerMinute: number | null;
    errorRate: number | null;
    p95Ms: number | null;
    updatedAt: Date | null;
    versionLabel: string | null;
}

/** Every stage's health from the same EB listing; unhealthy services carry no instance health or metrics. */
export interface AwsStageSummary {
    stage: Stage;
    health: Health;
    total: number;
    unhealthy: AwsServiceHealth[];
}

export type HealthEventSource = 'deploy' | 'eb';

export interface AwsHealthEvent {
    id: string;
    source: HealthEventSource;
    severity: 'info' | 'warning' | 'error';
    service: string;
    environmentName: string;
    message: string;
    at: Date;
}

export interface AwsAlarm {
    name: string;
    reason: string;
    since: Date | null;
}

/** One point per minute, oldest first, summed over every load balancer in the stage. */
export interface AwsMetricHistory {
    timestamps: Date[];
    requestsPerMinute: number[];
    errorRate: number[];
    p95Ms: (number | null)[];
    /** Null when no RDS instance is configured. */
    dbCpu: (number | null)[] | null;
}

export type AwsHealthSection = 'environments' | 'health' | 'metrics' | 'events' | 'alarms';

export interface AwsHealth {
    stage: Stage;
    generatedAt: Date;
    services: AwsServiceHealth[];
    stages: AwsStageSummary[];
    events: AwsHealthEvent[];
    /** Null when the alarms section failed (see errors). */
    alarms: AwsAlarm[] | null;
    /** Null when the metrics section failed (see errors). */
    metrics: AwsMetricHistory | null;
    /** Sections that failed on their own, usually a missing IAM permission. */
    errors: { section: AwsHealthSection; message: string }[];
}

type RawService = Omit<AwsServiceHealth, 'updatedAt'> & { updatedAt: string | null };

const toDate = (value: string | null) => (value ? new Date(value) : null);
const toService = (s: RawService): AwsServiceHealth => ({ ...s, updatedAt: toDate(s.updatedAt) });

/** Rejects with `request()`'s Response on failure; turn it into text with describeError. */
export async function getAwsHealth(stage: Stage): Promise<AwsHealth> {
    // no-store for the same reason as the Sentry call below: polled live data.
    const x = await request('api/system-health/aws', { query: { stage }, cache: 'no-store' }).then((r) => r.json());
    return {
        ...x,
        generatedAt: new Date(x.generatedAt),
        services: x.services.map(toService),
        stages: x.stages.map((s: Omit<AwsStageSummary, 'unhealthy'> & { unhealthy: RawService[] }) => ({
            ...s,
            unhealthy: s.unhealthy.map(toService),
        })),
        events: x.events.map((e: Omit<AwsHealthEvent, 'at'> & { at: string }) => ({ ...e, at: new Date(e.at) })),
        alarms:
            x.alarms?.map((a: Omit<AwsAlarm, 'since'> & { since: string | null }) => ({ ...a, since: toDate(a.since) })) ??
            null,
        metrics: x.metrics && { ...x.metrics, timestamps: x.metrics.timestamps.map((t: string) => new Date(t)) },
    };
}

// ---- Sentry ----

interface HealthResult<T> {
    data: T | null;
    error: string | null;
    fetchedAt: string;
}

interface SentryIssueDto {
    shortId: string;
    title: string;
    level: string;
    permalink: string;
    count: string;
    userCount: number;
    firstSeen: string | null;
    lastSeen: string | null;
    project: { slug: string } | null;
}

export interface SentryIssuesResult {
    issues: SentryIssue[] | null;
    error: string | null;
}

/** Never rejects: a failed call comes back as `error` so the page can say Sentry isn't connected. */
export async function getSentryIssues(): Promise<SentryIssuesResult> {
    try {
        const text = await request('api/system-health/sentry/issues', {
            // Live data, polled: never let the browser answer from its cache. Before this endpoint
            // existed the backend's SPA fallback served index.html here with no Cache-Control, and
            // browsers kept replaying that cached HTML.
            cache: 'no-store',
        }).then((x) => x.text());
        let res: HealthResult<SentryIssueDto[]>;
        try {
            res = JSON.parse(text);
        } catch {
            const preview = text.trim().slice(0, 80) || '(empty body)';
            return { issues: null, error: `MacGyver's backend sent a response that isn't JSON. It starts with: ${preview}` };
        }
        return {
            error: res.error,
            issues:
                res.data?.map((i) => ({
                    shortId: i.shortId,
                    title: i.title,
                    project: i.project?.slug ?? '',
                    level: i.level as SentryIssue['level'],
                    count: Number(i.count),
                    users: i.userCount,
                    lastSeen: new Date(i.lastSeen ?? Date.now()),
                    permalink: i.permalink,
                })) ?? null,
        };
    } catch (e) {
        console.error('[SystemHealth] loading Sentry issues failed', e);
        return { issues: null, error: describeError(e) };
    }
}

/** Turns whatever `request()` rejected with into a sentence that says what to do about it. */
export function describeError(e: unknown): string {
    if (e instanceof Response) {
        if (e.status === 401) return 'Your MacGyver session has expired. Log in again.';
        if (e.status === 403) return "Your MacGyver user doesn't have access to the system health data.";
        if (e.status >= 500) return `MacGyver's backend failed (${e.status}). Check the backend log for the cause.`;
        return `MacGyver's backend answered ${e.status} ${e.statusText}.`;
    }
    if (e instanceof SyntaxError)
        return "MacGyver's backend sent a response that isn't JSON. It may be restarting; try again in a moment.";
    if (e instanceof TypeError) return "Couldn't reach MacGyver's backend. Check that it's running.";
    return 'Something unexpected went wrong. The browser console has the details.';
}
